/* ─────────────────────────────────────────────────────────
 * ANIMATION STORYBOARD - Interface Craft Style
 *
 * SYSTEM A — Functional Flip (standard themes)
 *    0ms   card mounts (invisible, slightly scaled down)
 *  200ms   card fades in + subtle scale (gentle spring)
 *
 * On flip:
 *    0ms   user clicks/taps
 *  350ms   smooth flip with subtle spring
 *
 * SYSTEM B — Artifact Transition (gradient theme)
 *    0ms   card mounts (invisible, slightly below)
 *  300ms   card fades in + subtle upward drift
 *
 * On flip:
 *    0ms   user clicks/taps
 *  200ms   opacity crossfade with vertical shift
 * ───────────────────────────────────────────────────────── */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookingCardFrontCrafted, BookingCardFrontProps } from './BookingCardFrontCrafted';
import { BookingCardBack, BookingCardBackProps } from './BookingCardBack';
import { BookingCardGradientFront } from './BookingCardGradientFront';
import { BookingCardGradientBack } from './BookingCardGradientBack';
import { BookingCardRichGradientFront } from './BookingCardRichGradientFront';
import { BookingCardRichGradientBack } from './BookingCardRichGradientBack';
import { BookingCardSkyRippleFront } from './BookingCardSkyRippleFront';
import { BookingCardSkyRippleBack } from './BookingCardSkyRippleBack';
import { getThemeCrafted } from './themesCrafted';
import { useReducedMotion } from '@/hooks/useReducedMotion';

export interface BookingCardPayload {
  business_name: string;
  business_instagram_handle: string | null;
  business_logo_url?: string | null;
  booking_card_theme_key: string;
  client_full_name: string;
  displayed_visits: number;
  member_since_date: string | null;
  service_name: string | null;
  appointment_date: string;
  appointment_time: string;
  location_label: string | null;
  public_confirmation_code: string;
  tile_animation?: {
    enabled?: boolean;
    activeOpacity?: number;
    inactiveOpacity?: number;
    speedMultiplier?: number;
    rotationIntensity?: number;
  };
  center_image_url?: string | null;
  center_custom_text?: string;
}

interface BookingCardCraftedProps {
  payload: BookingCardPayload;
  className?: string;
}

/* ─────────────────────────────────────────────────────────
 * ANIMATION CONFIG - Subtle and refined
 * ───────────────────────────────────────────────────────── */

// Card entrance - gentle and professional
const CARD_ENTRANCE = {
  initialScale:  0.96,
  finalScale:    1.0,
  initialOpacity: 0,
  finalOpacity:   1,
  spring: { type: "spring" as const, stiffness: 260, damping: 26 },
};

// Flip - smooth and confident
const FLIP = {
  type: "spring" as const,
  stiffness: 340,
  damping: 30,
};

export function BookingCardCrafted({
  payload,
  className = '',
}: BookingCardCraftedProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const theme = getThemeCrafted(payload.booking_card_theme_key);
  const isGradientTheme = payload.booking_card_theme_key === 'gradient';
  const isRichGradientTheme = payload.booking_card_theme_key === 'rich_gradient';
  const isSkyRippleTheme = payload.booking_card_theme_key === 'sky_ripple';

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const frontProps: BookingCardFrontProps = {
    businessName: payload.business_name,
    businessInstagramHandle: payload.business_instagram_handle,
    businessLogoUrl: payload.business_logo_url,
    clientFullName: payload.client_full_name,
    displayedVisits: payload.displayed_visits,
    memberSinceDate: payload.member_since_date,
    publicConfirmationCode: payload.public_confirmation_code,
    theme,
    tileAnimation: payload.tile_animation,
    centerImageUrl: payload.center_image_url,
    centerCustomText: payload.center_custom_text,
  };

  const backProps: BookingCardBackProps = {
    serviceName: payload.service_name,
    appointmentDate: payload.appointment_date,
    appointmentTime: payload.appointment_time,
    locationLabel: payload.location_label,
    publicConfirmationCode: payload.public_confirmation_code,
    theme,
  };

  // SYSTEM B — Artifact Transition (Sky Ripple theme with shader)
  if (isSkyRippleTheme) {
    return (
      <div className={`w-[88%] sm:w-full max-w-sm mx-auto py-8 px-4 sm:py-0 sm:px-0 ${className}`}>
        {/* Entrance animation */}
        <motion.div
          initial={prefersReducedMotion ? {} : {
            opacity: 0,
            translateY: 8,
          }}
          animate={{
            opacity: 1,
            translateY: 0,
          }}
          transition={{
            duration: 0.3,
            ease: 'easeOut',
          }}
        >
          <div className="relative w-full" style={{ aspectRatio: '2/3' }}>
            <button
              onClick={handleFlip}
              className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                outlineColor: theme.accentColor,
              }}
              aria-label={isFlipped ? 'Show front of card' : 'Show back of card'}
            >
              <AnimatePresence mode="wait">
                {isFlipped ? (
                  <motion.div
                    key="back"
                    className="absolute inset-0"
                    initial={prefersReducedMotion ? {} : { opacity: 0, translateY: 4 }}
                    animate={{ opacity: 1, translateY: 0 }}
                    exit={{ opacity: 0, translateY: -4 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.2, ease: 'easeOut' }}
                  >
                    <BookingCardSkyRippleBack
                      serviceName={payload.service_name}
                      appointmentDate={payload.appointment_date}
                      appointmentTime={payload.appointment_time}
                      locationLabel={payload.location_label}
                      publicConfirmationCode={payload.public_confirmation_code}
                      theme={theme}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="front"
                    className="absolute inset-0"
                    initial={prefersReducedMotion ? {} : { opacity: 0, translateY: 4 }}
                    animate={{ opacity: 1, translateY: 0 }}
                    exit={{ opacity: 0, translateY: -4 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.2, ease: 'easeOut' }}
                  >
                    <BookingCardSkyRippleFront
                      businessName={payload.business_name}
                      businessInstagramHandle={payload.business_instagram_handle}
                      businessLogoUrl={payload.business_logo_url}
                      clientFullName={payload.client_full_name}
                      displayedVisits={payload.displayed_visits}
                      memberSinceDate={payload.member_since_date}
                      serviceName={payload.service_name}
                      appointmentDate={payload.appointment_date}
                      appointmentTime={payload.appointment_time}
                      locationLabel={payload.location_label}
                      centerImageUrl={payload.center_image_url}
                      centerCustomText={payload.center_custom_text}
                      onFlip={handleFlip}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </motion.div>
        <div className="text-center mt-4 text-xs" style={{ color: 'rgba(0,0,0,0.35)' }}>
          Tap to {isFlipped ? 'see front' : 'see details'}
        </div>
      </div>
    );
  }

  // SYSTEM B — Artifact Transition (Rich Gradient theme)
  if (isRichGradientTheme) {
    return (
      <div className={`w-[88%] sm:w-full max-w-sm mx-auto py-8 px-4 sm:py-0 sm:px-0 ${className}`}>
        {/* Entrance animation */}
        <motion.div
          initial={prefersReducedMotion ? {} : {
            opacity: 0,
            translateY: 8,
          }}
          animate={{
            opacity: 1,
            translateY: 0,
          }}
          transition={{
            duration: 0.3,
            ease: 'easeOut',
          }}
        >
          <div className="relative w-full" style={{ aspectRatio: '2/3' }}>
            <button
              onClick={handleFlip}
              className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                outlineColor: theme.accentColor,
              }}
              aria-label={isFlipped ? 'Show front of card' : 'Show back of card'}
            >
              <AnimatePresence mode="wait">
                {isFlipped ? (
                  <motion.div
                    key="back"
                    className="absolute inset-0"
                    initial={prefersReducedMotion ? {} : { opacity: 0, translateY: 4 }}
                    animate={{ opacity: 1, translateY: 0 }}
                    exit={{ opacity: 0, translateY: -4 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.2, ease: 'easeOut' }}
                  >
                    <BookingCardRichGradientBack
                      serviceName={payload.service_name}
                      appointmentDate={payload.appointment_date}
                      appointmentTime={payload.appointment_time}
                      locationLabel={payload.location_label}
                      publicConfirmationCode={payload.public_confirmation_code}
                      theme={theme}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="front"
                    className="absolute inset-0"
                    initial={prefersReducedMotion ? {} : { opacity: 0, translateY: 4 }}
                    animate={{ opacity: 1, translateY: 0 }}
                    exit={{ opacity: 0, translateY: -4 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.2, ease: 'easeOut' }}
                  >
                    <BookingCardRichGradientFront
                      businessName={payload.business_name}
                      businessInstagramHandle={payload.business_instagram_handle}
                      businessLogoUrl={payload.business_logo_url}
                      clientFullName={payload.client_full_name}
                      displayedVisits={payload.displayed_visits}
                      memberSinceDate={payload.member_since_date}
                      publicConfirmationCode={payload.public_confirmation_code}
                      theme={theme}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </motion.div>
        <div className="text-center mt-4 text-xs" style={{ color: 'rgba(0,0,0,0.35)' }}>
          Tap to {isFlipped ? 'see front' : 'see details'}
        </div>
      </div>
    );
  }

  // SYSTEM B — Artifact Transition (Gradient theme)
  if (isGradientTheme) {
    return (
      <div className={`w-[88%] sm:w-full max-w-sm mx-auto py-8 px-4 sm:py-0 sm:px-0 ${className}`}>
        {/* Entrance animation */}
        <motion.div
          initial={prefersReducedMotion ? {} : {
            opacity: 0,
            translateY: 8,
          }}
          animate={{
            opacity: 1,
            translateY: 0,
          }}
          transition={{
            duration: 0.3,
            ease: 'easeOut',
          }}
        >
          <div className="relative w-full" style={{ aspectRatio: '2/3' }}>
            <button
              onClick={handleFlip}
              className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                outlineColor: theme.accentColor,
              }}
              aria-label={isFlipped ? 'Show front of card' : 'Show back of card'}
            >
              <AnimatePresence mode="wait">
                {isFlipped ? (
                  <motion.div
                    key="back"
                    className="absolute inset-0"
                    initial={prefersReducedMotion ? {} : { opacity: 0, translateY: 4 }}
                    animate={{ opacity: 1, translateY: 0 }}
                    exit={{ opacity: 0, translateY: -4 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.2, ease: 'easeOut' }}
                  >
                    <BookingCardGradientBack
                      serviceName={payload.service_name}
                      appointmentDate={payload.appointment_date}
                      appointmentTime={payload.appointment_time}
                      locationLabel={payload.location_label}
                      publicConfirmationCode={payload.public_confirmation_code}
                      theme={theme}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="front"
                    className="absolute inset-0"
                    initial={prefersReducedMotion ? {} : { opacity: 0, translateY: 4 }}
                    animate={{ opacity: 1, translateY: 0 }}
                    exit={{ opacity: 0, translateY: -4 }}
                    transition={{ duration: prefersReducedMotion ? 0 : 0.2, ease: 'easeOut' }}
                  >
                    <BookingCardGradientFront
                      businessName={payload.business_name}
                      businessInstagramHandle={payload.business_instagram_handle}
                      businessLogoUrl={payload.business_logo_url}
                      clientFullName={payload.client_full_name}
                      displayedVisits={payload.displayed_visits}
                      memberSinceDate={payload.member_since_date}
                      publicConfirmationCode={payload.public_confirmation_code}
                      theme={theme}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </motion.div>
        <div className="text-center mt-4 text-xs" style={{ color: 'rgba(0,0,0,0.35)' }}>
          Tap to {isFlipped ? 'see front' : 'see details'}
        </div>
      </div>
    );
  }

  if (prefersReducedMotion) {
    return (
      <div className={`w-[88%] sm:w-full max-w-sm mx-auto py-8 px-4 sm:py-0 sm:px-0 ${className}`}>
        <div className="relative w-full" style={{ aspectRatio: '2/3' }}>
          <button
            onClick={handleFlip}
            className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{
              '--tw-ring-color': theme.accentColor,
            } as React.CSSProperties}
            aria-label={isFlipped ? 'Show front of card' : 'Show back of card'}
          >
            {isFlipped ? (
              <BookingCardBack {...backProps} />
            ) : (
              <BookingCardFrontCrafted {...frontProps} />
            )}
          </button>
        </div>
        <div className="text-center mt-4 text-xs uppercase tracking-wider" style={{ color: theme.textSecondary }}>
          Tap to {isFlipped ? 'see front' : 'see details'}
        </div>
      </div>
    );
  }

  return (
    <div className={`w-[88%] sm:w-full max-w-sm mx-auto py-8 px-4 sm:py-0 sm:px-0 ${className}`}>
      <motion.div
        initial={{
          opacity: CARD_ENTRANCE.initialOpacity,
          scale: CARD_ENTRANCE.initialScale,
        }}
        animate={{
          opacity: CARD_ENTRANCE.finalOpacity,
          scale: CARD_ENTRANCE.finalScale,
        }}
        transition={CARD_ENTRANCE.spring}
      >
        <motion.div
          className="relative w-full cursor-pointer"
          style={{ aspectRatio: '2/3', perspective: '1000px' }}
          onClick={handleFlip}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleFlip();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={isFlipped ? 'Show front of card' : 'Show back of card'}
        >
          <motion.div
            className="relative w-full h-full"
            style={{
              transformStyle: 'preserve-3d',
            }}
            animate={{
              rotateY: isFlipped ? 180 : 0,
            }}
            transition={FLIP}
          >
            <div
              className="absolute w-full h-full"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
            >
              <BookingCardFrontCrafted {...frontProps} />
            </div>

            <div
              className="absolute w-full h-full"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
              }}
            >
              <BookingCardBack {...backProps} />
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        className="text-center mt-4 text-xs uppercase tracking-wider"
        style={{ color: theme.textSecondary }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        Tap to {isFlipped ? 'flip back' : 'see details'}
      </motion.div>
    </div>
  );
}
